import AnalyticsCategory = ToroAnalyticsEnums.AnalyticsCategory;
import AnalyticsEvent = ToroAnalyticsEnums.AnalyticsEvent;

import * as Highcharts from 'highcharts';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { finalize, take } from 'rxjs/operators';
import { AnalyticsService } from '../../../../../common/services/analytics.service';
import { BroadcastService } from '../../../../../common/services/broadcast.service';
import { DashMessageService } from '../../../../../common/services/dash-message.service';
import { DashUserInfo } from '../../../../../api/dash-user/models/dash-user-info.model';
import { DashUserManagerService } from '../../../../../api/dash-user/dash-user-manager.service';
import { DashUserPreferences } from '../../../../../api/dash-user/models/dash-user-preferences.model';
import { DeviceManagerService } from '../../../../../common/services/device-manager.service';
import { environment } from '../../../../../../environments/environment';
import { MayaArea } from '../../../../../api/maya/models/maya-area.model';
import { MayaManagerService } from '../../../../../api/maya/maya-manager.service';
import { MayaSite } from '../../../../../api/maya/models/maya-site.model';
import { MayaSitesAndAreas } from '../../../../../api/maya/models/maya-sites-and-areas.model';
import { MayaSubstanceMetrics } from '../../../../../api/maya/models/maya-substance-metrics.model';
import { MayaSubstancesMetrics } from '../../../../../api/maya/models/maya-substances-metrics.model';
import { SelectItem } from 'primeng/api';
import { ToroAnalyticsEnums } from '../../../../../common/enumerations/analytics.enums';
import { ToroDashboardWidget } from '../../toro-dashboard-widget';
import { ToroUtils } from '../../../../../common/utils/_toro.utils';
import { TranslateService } from '@ngx-translate/core';
import { untilDestroyed } from '@ngneat/until-destroy';

const More = require('highcharts/highcharts-more');
More(Highcharts);

@Component({
    selector: 'toro-widget-maya',
    templateUrl: './widget-maya.component.html',
    styleUrls: ['./widget-maya.component.less']
})
export class WidgetMayaComponent extends ToroDashboardWidget implements OnInit {
    @ViewChild('chartContainer', { static: false }) chartContainer: ElementRef;

    iconColor = 'black';
    title = 'WIDGET.MAYA';

    Highcharts = Highcharts;
    gaugeOptions: any = null;
    isGaugeLoaded = true;

    protected chartContainerWidth = 0;
    protected chartContainerHeight = 0;
    protected substanceN: MayaSubstanceMetrics;
    protected substanceP: MayaSubstanceMetrics;
    protected substanceK: MayaSubstanceMetrics;
    protected coursesSelectList: SelectItem[] = [];
    protected areasSelectList: SelectItem[] = [];
    protected isLoadingData = false;
    protected isValidCompanyApiKeySet = false;
    protected isApiKeyDialogDisplayed = false;
    protected newMayaApiToken: string;
    protected dashUserPrefs: DashUserPreferences;
    protected spinnerText = '';

    private mayaSites: MayaSitesAndAreas;

    private _selectedSite: MayaSite;
    protected set selectedSite(value: MayaSite) {
        if (!this.isBusy && value?.idsite != this._selectedSite?.idsite) {
            this.isLoadingData = true;
        }

        this._selectedSite = value;
        this.setAreasSelectList();

        this.dashUserPrefs.mayaSelectedSiteId = value.idsite;
        this.dashUserManager.updateDashUserPreferences(this.dashUserPrefs).subscribe()
    }

    protected get selectedSite(): MayaSite {
        return this._selectedSite;
    }

    protected _selectedArea: MayaArea;
    protected set selectedArea(value: MayaArea) {
        if (!this.isBusy && value?.idtag != this._selectedArea?.idtag) {
            this.isLoadingData = true;
        }

        this._selectedArea = value;
        this.setAreaData();

        this.dashUserPrefs.mayaSelectedAreaId = value?.idtag;
        this.dashUserManager.updateDashUserPreferences(this.dashUserPrefs).subscribe()
    }

    protected get selectedArea(): MayaArea {
        return this._selectedArea;
    }

    private _mayaApiToken = null;
    set mayaApiToken(value: string) {
        this._mayaApiToken = (value != null && value !== '') ? value : null;
    }

    get mayaApiToken(): string {
        return this._mayaApiToken;
    }

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(protected analyticsService: AnalyticsService,
                protected broadcastService: BroadcastService,
                private dashMessageService: DashMessageService,
                protected dashUserManager: DashUserManagerService,
                protected deviceManager: DeviceManagerService,
                private mayaManager: MayaManagerService,
                protected translateService: TranslateService,
    ) {
        super(analyticsService, broadcastService, dashUserManager, deviceManager, translateService);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.broadcastService.setupMayaApiTokenChanged
            .pipe(untilDestroyed(this))
            .subscribe((integrationKey: string) => {
                this.getWidgetData(true);
            });
    }

    // =========================================================================================================================================================
    // Base Class Overrides
    // =========================================================================================================================================================

    get analyticsWidgetName(): string {
        return AnalyticsEvent.MayaWidgetName;
    }

    protected getWidgetData(isManualRefresh) {
        if (isManualRefresh) { this.isBusy = true; }

        this.dashUserManager.getDashUserInfo()
            .pipe(take(1))
            .subscribe((dashUserInfo: DashUserInfo) => {
                this.dashUserPrefs = dashUserInfo?.preferences;
                if (!this.dashUserPrefs || this.dashUserPrefs.mayaApiToken == null) {
                    this.setNoAccountDisplay();
                    return;
                }

                this.mayaApiToken = this.dashUserPrefs.mayaApiToken;

                this.mayaManager.getMayaSitesAndAreas(this.mayaApiToken)
                    .pipe(
                        take(1),
                        finalize(() => {
                            this.isBusy = false;
                            this.isWidgetInitialized = true;
                        })
                    )
                    .subscribe({
                        next: (sites: MayaSitesAndAreas) => {
                            this.mayaSites = sites;

                            // Load previously selected Site and Area on initial widget load.
                            if (!this.isWidgetInitialized && this.dashUserPrefs?.mayaSelectedSiteId != null) {
                                this._selectedSite = this.mayaSites?.data?.find(site => site.idsite == this.dashUserPrefs.mayaSelectedSiteId);

                                if (this._selectedSite != null && this.dashUserPrefs?.mayaSelectedAreaId != null) {
                                    this._selectedArea = this._selectedSite.tags?.find(tag => tag?.idtag == this.dashUserPrefs.mayaSelectedAreaId);
                                }
                            }

                            this.setSitesSelectList();
                            this.lastUpdateTimestamp = new Date();
                            this.clearIsUnableToFetchData();
                        },
                        error: err => {
                            this.setIsUnableToFetchData('SPECIAL_MSG.VERIFY_STRIKE_GUARD_ID');
                            this.dashMessageService.showWidgetDataFetchErrorMessage(this.title);
                        }
                    })
            })
    }

    protected setWidgetMenu() {
        super.setWidgetMenu();

        this.widgetMenuItems.unshift(
            {
                label: ToroUtils.Translate.instant('CASE_SENSITIVE.ENTER_MAYA_API_KEY'),
                icon: 'pi pi-fw pi-key',
                command: this.showApiKeyDialog.bind(this)
            },
            { separator: true }
        );
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    onNavigateToSite() {
        this.broadcastService.toggleSystemOverlay.next({ show: true, text: 'STRINGS.NAVIGATING_TO_MAYA' });
        setTimeout(() => window.open(environment.strikeGuardSiteUrl, '_blank'), 1000);
        setTimeout(() => this.broadcastService.toggleSystemOverlay.next({ show: false }), 2000);
    }

    onSetApiKey(apiKey: string) {
        this.isApiKeyDialogDisplayed = false;

        // TODO: Temporary?
        if (apiKey != null) {
            this.newMayaApiToken = apiKey;
        }

        // If the Course Id was not changed, don't do anything.
        if (this.mayaApiToken === this.newMayaApiToken) { return; }

        this.mayaApiToken = this.newMayaApiToken;
        this.dashUserPrefs.mayaApiToken = this.mayaApiToken;

        this.dashUserManager.updateDashUserPreferences(this.dashUserPrefs)
            .subscribe({
                next: () => {
                    if (this.mayaApiToken != null && this.mayaApiToken != '') {
                        this.isBusy = true;
                        this.spinnerText = `${this.translateService.instant('STRINGS.RETRIEVING_DATA_FOR_NEW_API_TOKEN')}...`;
                        this.getWidgetData(true);
                    } else {
                        this.setNoAccountDisplay();
                    }

                    const eventDetails = `Maya API Token: ${this.newMayaApiToken}`
                    this.analyticsService.widgetEvent(AnalyticsEvent.MayaWidgetSaveApiKeyDialog, AnalyticsCategory.Interaction, this.analyticsWidgetName, eventDetails);
                },
                error: () => {
                    this.resetBusy();
                    this.dashMessageService.showGenericSaveErrorMessage();
                }
            });
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private setSitesSelectList() {
        this.coursesSelectList = [];

        if (this.mayaSites?.data?.length > 0) {
            this.mayaSites.data.forEach((item) => {
                this.coursesSelectList.push({ label: item.name, value: item })
            })

            if (!this.selectedSite) {
                this.selectedSite = this.mayaSites.data[0];
            } else {
                this.selectedSite = this.coursesSelectList.find(site => site.value.idsite == this.selectedSite.idsite)?.value || this.mayaSites.data[0];
            }
        }
    }

    private setAreasSelectList() {
        this.areasSelectList = [];

        if (this.selectedSite?.tags != null) {
            const sortedAreas = this.selectedSite.tags.sort((a, b) => a.tag_name.localeCompare(b.tag_name));

            sortedAreas.forEach((area: MayaArea) => {
                this.areasSelectList.push({ label: area.tag_name, value: area })
            })

            if (!this.selectedArea) {
                this.selectedArea = this.areasSelectList[0]?.value;
            } else {
                this.selectedArea = this.areasSelectList.find(area => area.value?.idtag == this.selectedArea?.idtag)?.value;
            }
        }
    }

    private setAreaData() {
        if (!this.selectedArea || !this.selectedSite || this.dashUserPrefs?.mayaSelectedSiteId == null) {
            this.clearDataDisplay();
            return;
        }

        this.mayaManager.getMayaSubstancesMetrics((new Date()).getFullYear(), this.selectedSite.idsite, this.selectedArea?.idtag, this.dashUserPrefs.mayaApiToken)
            .pipe(
                take(1),
                finalize(() => {
                    this.isLoadingData = false;
                    this.isBusy = false;
                })
            )
            .subscribe({
                next: (metrics: MayaSubstancesMetrics) => {
                    this.substanceN = metrics.data.find(s => s.substance == 'N');
                    this.substanceP = metrics.data.find(s => s.substance == 'P');
                    this.substanceK = metrics.data.find(s => s.substance == 'K');

                    if (this.substanceN == null) {
                        this.substanceN = this.getEmptySubstance('N');
                    }

                    if (this.substanceP == null) {
                        this.substanceP = this.getEmptySubstance('P');
                    }

                    if (this.substanceK == null) {
                        this.substanceK = this.getEmptySubstance('K');
                    }

                },
                error: err => {
                    this.clearDataDisplay();
                }
            })
    }

    private clearDataDisplay() {
        this.substanceN = this.getEmptySubstance('N');
        this.substanceP = this.getEmptySubstance('P');
        this.substanceK = this.getEmptySubstance('K');
    }

    private getEmptySubstance(substance: string): MayaSubstanceMetrics {
        return new MayaSubstanceMetrics({
            substance: substance,
            spraying_value: 0,
            planned_value_current: 0,
            planned_value_total: 0
        })
    }

    private showApiKeyDialog() {
        this.newMayaApiToken = this.mayaApiToken;
        this.isApiKeyDialogDisplayed = true;

        const eventDetails = `API Key: ${this.mayaApiToken}`;
        this.analyticsService.widgetEvent(AnalyticsEvent.MayaWidgetShowApiKeyDialog, AnalyticsCategory.Interaction, this.analyticsWidgetName, eventDetails);
    }

    protected resetBusy() {
        this.isBusy = false;
        this.spinnerText = null;
    }

    private setNoAccountDisplay() {
        this.isValidCompanyApiKeySet = false;
        this.setIsUnableToFetchData('SPECIAL_MSG.PROVIDE_MAYA_API_KEY');
        this.resetBusy();
    }
}
